import styled from 'styled-components';
import { Card } from '@shared/ui/Card';
import {
  assignmentStatusLabel,
  assignmentStatusTone,
  fmtDueDate,
  fmtMeetingDate,
  fmtTimecodeSec,
} from '@entities/meetings';

const Section = styled.section`
  & + & {
    margin-top: ${({ theme }) => theme.spacing(8)};
  }
`;

const SectionHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  flex-wrap: wrap;
`;

const SectionLabel = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ActionBtn = styled.button<{ $variant?: 'primary' | 'ghost' }>`
  font-size: 13px;
  font-weight: 600;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(4)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  cursor: pointer;
  border: 1px solid
    ${({ theme, $variant }) => ($variant === 'ghost' ? theme.colors.border : theme.colors.primary)};
  background: ${({ theme, $variant }) => ($variant === 'ghost' ? 'transparent' : theme.colors.primary)};
  color: ${({ theme, $variant }) => ($variant === 'ghost' ? theme.colors.text.secondary : '#fff')};

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
  &:hover:not(:disabled) {
    filter: brightness(1.06);
  }
`;

const KindBlock = styled.div`
  & + & {
    margin-top: ${({ theme }) => theme.spacing(4)};
  }
`;

const KindTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const ItemRow = styled.div`
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.primary};
  padding: ${({ theme }) => `${theme.spacing(1)} 0`};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const ItemSub = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const EmptyHint = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.secondary};
  max-width: 640px;
`;

const AssignmentRow = styled(Card)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;

  & + & {
    margin-top: ${({ theme }) => theme.spacing(2)};
  }
`;

const AssignmentText = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const AssignmentMeta = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: ${({ theme }) => theme.spacing(0.5)};
`;

const StatusTag = styled.span<{ $tone: 'success' | 'danger' | 'neutral' }>`
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  padding: ${({ theme }) => `${theme.spacing(0.5)} ${theme.spacing(2)}`};
  border-radius: ${({ theme }) => theme.radius.pill};
  color: ${({ theme, $tone }) =>
    $tone === 'neutral' ? theme.colors.text.muted : theme.colors.status[$tone]};
  background: ${({ theme, $tone }) =>
    $tone === 'neutral' ? theme.colors.surfaceHover : `${theme.colors.status[$tone]}18`};
`;

const TranscriptBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
  max-height: 420px;
  overflow-y: auto;
  padding-right: ${({ theme }) => theme.spacing(2)};
`;

const SegRow = styled.div`
  font-size: 13px;
  line-height: 1.5;
`;

const SegMeta = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-right: ${({ theme }) => theme.spacing(2)};
  font-variant-numeric: tabular-nums;
`;

const SegText = styled.span`
  color: ${({ theme }) => theme.colors.text.primary};
`;

export interface SeriesDecision {
  item_id: string;
  timecode?: string | null;
  text: string;
  meeting_title: string;
  scheduled_at?: string | null;
  department_name?: string | null;
}

export interface SeriesAssignment {
  id: string;
  text: string;
  status: string;
  assignee_name?: string | null;
  due_date?: string | null;
  due_text?: string | null;
  department_name?: string | null;
}

export interface SeriesSummaryData {
  decisions: SeriesDecision[];
  assignments: SeriesAssignment[];
}

export interface MeetingSeriesSummarySectionProps {
  seriesId: string;
  showSeries: boolean;
  seriesSummary?: SeriesSummaryData | null;
}

export function MeetingSeriesSummarySection({
  seriesId,
  showSeries,
  seriesSummary,
}: MeetingSeriesSummarySectionProps) {
  if (!seriesId || !showSeries) return null;

  return (
    <Section>
      <SectionLabel>Сводка серии</SectionLabel>
      {seriesSummary ? (
        <>
          <KindBlock>
            <KindTitle>Действующие решения</KindTitle>
            {seriesSummary.decisions.length > 0 ? (
              seriesSummary.decisions.map((d) => (
                <ItemRow key={d.item_id}>
                  {d.timecode ? `[${d.timecode}] ` : ''}
                  {d.text}
                  <ItemSub>
                    {' '}
                    · {d.meeting_title}
                    {d.scheduled_at ? `, ${fmtMeetingDate(d.scheduled_at)}` : ''}
                    {d.department_name ? ` · отдел: ${d.department_name}` : ''}
                  </ItemSub>
                </ItemRow>
              ))
            ) : (
              <EmptyHint>Действующих решений нет.</EmptyHint>
            )}
          </KindBlock>
          <KindBlock>
            <KindTitle>Открытые поручения</KindTitle>
            {seriesSummary.assignments.length > 0 ? (
              seriesSummary.assignments.map((a) => (
                <AssignmentRow key={a.id}>
                  <div>
                    <AssignmentText>{a.text}</AssignmentText>
                    <AssignmentMeta>
                      Ответственный: {a.assignee_name ?? 'уточнить'} · Срок:{' '}
                      {a.due_date ? fmtDueDate(a.due_date) : (a.due_text ?? 'уточнить')}
                      {a.department_name ? ` · отдел: ${a.department_name}` : ''}
                    </AssignmentMeta>
                  </div>
                  <StatusTag $tone={assignmentStatusTone(a.status)}>
                    {assignmentStatusLabel(a.status)}
                  </StatusTag>
                </AssignmentRow>
              ))
            ) : (
              <EmptyHint>Открытых поручений нет.</EmptyHint>
            )}
          </KindBlock>
        </>
      ) : (
        <EmptyHint>Сводка серии загружается…</EmptyHint>
      )}
    </Section>
  );
}

export interface TranscriptSegment {
  id: string;
  t_start: number;
  speaker: string;
  text: string;
}

export interface MeetingTranscriptSectionProps {
  showTranscript: boolean;
  onToggleTranscript: () => void;
  segments: TranscriptSegment[];
}

export function MeetingTranscriptSection({
  showTranscript,
  onToggleTranscript,
  segments,
}: MeetingTranscriptSectionProps) {
  return (
    <Section>
      <SectionHead>
        <SectionLabel>Транскрипт</SectionLabel>
        <ActionBtn $variant="ghost" onClick={onToggleTranscript}>
          {showTranscript ? 'Скрыть' : 'Показать'}
        </ActionBtn>
      </SectionHead>
      {showTranscript &&
        (segments.length > 0 ? (
          <TranscriptBox>
            {segments.map((s) => (
              <SegRow key={s.id}>
                <SegMeta>
                  [{fmtTimecodeSec(s.t_start)}] {s.speaker}
                </SegMeta>
                <SegText>{s.text}</SegText>
              </SegRow>
            ))}
          </TranscriptBox>
        ) : (
          <EmptyHint>Транскрипт не готов или пуст.</EmptyHint>
        ))}
    </Section>
  );
}
